<section aria-labelledby="billing-ticket-heading">
  <h2 id="billing-ticket-heading">Create Billing and Payment Ticket</h2>

  @if (isLoading) {
    <div class="my-4 animate-pulse" role="status" aria-label="Loading form">
      <div class="mb-2 h-4 w-32 rounded bg-gray-200"></div>
      <div class="mb-6 h-12 w-full rounded bg-gray-200"></div>
      <div class="mb-2 h-4 w-28 rounded bg-gray-200"></div>
      <div class="mb-6 h-32 w-full rounded bg-gray-200"></div>
      <div class="h-12 w-full rounded bg-gray-200"></div>
    </div>
  } @else if (billTicketGroup) {
    <form
      [formGroup]="billTicketGroup"
      [ngClass]="{ 'form-submit-attempt': formSubmitAttempted() }"
    >
      @if (invoices) {
        <div class="mt-4">
          <label class="comcast-input-label" for="invoice-date-btn">
            Invoice date
            <span class="comcast-required-marker" aria-hidden="true">*</span>
          </label>
          <div class="relative mt-2">
            <button
              id="invoice-date-btn"
              type="button"
              class="comcast-select-trigger"
              [class.comcast-select-trigger--open]="isInvoiceDropdownOpen()"
              [class.comcast-select-trigger--filled]="!!selectedInvoiceLabel"
              [class.comcast-input-invalid]="shouldShowError('invoiceDate')"
              [attr.aria-expanded]="isInvoiceDropdownOpen()"
              aria-haspopup="listbox"
              aria-controls="invoice-listbox"
              aria-required="true"
              [attr.aria-invalid]="shouldShowError('invoiceDate')"
              [attr.aria-describedby]="
                shouldShowError('invoiceDate') ? 'invoice-date-error' : null
              "
              (click)="toggleInvoiceDropdown($event)"
            >
              <span [class.text-brand-muted]="!selectedInvoiceLabel">
                {{ selectedInvoiceLabel || 'Please select' }}
              </span>
              <mat-icon class="comcast-select-icon" aria-hidden="true">
                {{
                  isInvoiceDropdownOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
                }}
              </mat-icon>
            </button>

            @if (isInvoiceDropdownOpen()) {
              <!-- a11y listbox pattern: docs/accessibility/custom-listbox.md -->
              <div
                id="invoice-listbox"
                class="comcast-select-panel"
                role="listbox"
                aria-label="Invoice dates"
                [class.comcast-select-panel--open]="isInvoiceDropdownOpen()"
                [class.comcast-select-panel--invalid]="
                  shouldShowError('invoiceDate')
                "
                (click)="$event.stopPropagation()"
              >
                <div class="comcast-select-search">
                  <mat-icon
                    class="comcast-select-search-icon"
                    aria-hidden="true"
                    >search</mat-icon
                  >
                  <input
                    type="text"
                    placeholder="Search"
                    aria-label="Search invoice dates"
                    [value]="invoiceSearchTerm()"
                    (input)="onSearchInput($event)"
                    class="comcast-select-search-input"
                    appAutofocus
                  />
                </div>
                <div class="comcast-select-options">
                  @for (
                    invoice of filteredInvoices;
                    track invoice.ContentDocument.Title
                  ) {
                    <div
                      class="comcast-select-option"
                      role="option"
                      tabindex="0"
                      [attr.aria-selected]="
                        billTicketGroup.value.invoiceDate ===
                        invoice.ContentDocument.Title
                      "
                      [class.comcast-select-option--selected]="
                        billTicketGroup.value.invoiceDate ===
                        invoice.ContentDocument.Title
                      "
                      (click)="
                        selectInvoice(invoice.ContentDocument.Title, $event)
                      "
                      (keydown.enter)="
                        selectInvoice(invoice.ContentDocument.Title, $event)
                      "
                      (keydown.space)="
                        selectInvoice(invoice.ContentDocument.Title, $event)
                      "
                    >
                      {{
                        invoice.ContentDocument.Title.slice(0, 10)
                          | date: 'longDate'
                      }}
                    </div>
                  }
                  @if (!filteredInvoices.length) {
                    <div
                      class="comcast-select-empty"
                      role="option"
                      aria-disabled="true"
                    >
                      No results found
                    </div>
                  }
                </div>
              </div>
            }
          </div>
          @if (shouldShowError('invoiceDate')) {
            <div
              id="invoice-date-error"
              class="comcast-validation-error"
              role="alert"
            >
              {{ inlineErrors.invoiceDate }}
            </div>
          }
        </div>
      }

      <div class="mt-4">
        <label class="comcast-input-label" for="summary-textarea">
          Issue summary
          <span class="comcast-required-marker" aria-hidden="true">*</span>
        </label>
        <textarea
          id="summary-textarea"
          formControlName="summary"
          class="comcast-textarea"
          [ngClass]="{ 'comcast-input-invalid': shouldShowError('summary') }"
          rows="5"
          maxlength="400"
          placeholder="Please describe your issue..."
          aria-required="true"
          [attr.aria-invalid]="shouldShowError('summary')"
          [attr.aria-describedby]="
            shouldShowError('summary')
              ? 'summary-error summary-count'
              : 'summary-count'
          "
          (keyup)="canSubmit()"
        ></textarea>
        <div class="mt-1 flex items-center justify-between">
          @if (shouldShowError('summary')) {
            <div
              id="summary-error"
              class="comcast-validation-error !mt-0"
              role="alert"
            >
              {{ inlineErrors.summary }}
            </div>
          } @else {
            <div></div>
          }
          <div
            id="summary-count"
            class="comcast-textarea-count !mt-0"
            aria-live="polite"
            aria-atomic="true"
          >
            {{ billTicketGroup.get('summary').value?.length || 0 }} / 400
          </div>
        </div>
      </div>

      @if (submissionError) {
        <div
          class="comcast-error-alert mt-4"
          role="alert"
          aria-live="assertive"
        >
          <mat-icon
            fontSet="material-icons-outlined"
            class="comcast-error-alert-icon"
            aria-hidden="true"
            >warning</mat-icon
          >
          <span class="comcast-error-alert-text">
            {{
              errorMessage || 'Unable to submit the ticket. Please try again!'
            }}
          </span>
        </div>
      }

      <div class="mt-6">
        <button
          class="btn-primary w-full"
          (click)="submitTicket()"
          [disabled]="isLoading || isSubmitting"
          [attr.aria-busy]="isSubmitting"
          [attr.aria-label]="
            isSubmitting ? 'Submitting ticket, please wait' : 'Submit ticket'
          "
        >
          @if (isSubmitting) {
            <mat-spinner
              [diameter]="20"
              color="accent"
              aria-hidden="true"
            ></mat-spinner>
          } @else {
            Submit ticket
          }
        </button>
      </div>
    </form>
  }
</section>
